import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { RedirectError } from '#src/shared/error';

import { differenceInDays } from '#src/app/shared/utils/dates';
import { isEligibleForTrafficPercentage } from '#src/app/shared/utils/featureRollout';

import type { PetitionDetailsDataQuery, PetitionDetailsDataQueryVariables } from './api.graphql';
import { PetitionDetailsData } from './api.graphql';
import { getPetitionAdvocates } from './getPetitionAdvocates';
import type { AdvocatesForPetitionQuery } from './getPetitionAdvocates/petitionAdvocates.graphql';
import { getPetitionStrengthScore } from './getPetitionStrengthScore';
import { getSupporterVoicesVideos } from './supporterVoicesVideos';
import type { PetitionDetailsPageData } from './types';

async function fetchPetitionDetails(utilityContext: UtilityContext, variables: PetitionDetailsDataQueryVariables) {
	const {
		gql: { fetch },
	} = utilityContext;
	const { data } = await fetch<PetitionDetailsDataQuery, PetitionDetailsDataQueryVariables>({
		query: PetitionDetailsData,
		variables,
		rejectOnError: true,
		// to separate query from other batched queries
		batched: false,
	});

	return data;
}

function normalizePetition(
	petition: NonNullable<PetitionDetailsDataQuery['petition']>,
): PetitionDetailsPageData['petition'] {
	return {
		...petition,
		dmsWithLegislativeBodiesConnection: {
			pageInfo: { hasNextPage: petition.dmsWithLegislativeBodiesConnection.pageInfo.hasNextPage },
			totalCount: petition.dmsWithLegislativeBodiesConnection.totalCount,
			nodes: petition.dmsWithLegislativeBodiesConnection.nodes,
		},
	};
}

export async function getPetitionDetailsPageData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		locale,
		countryCode,
	}: {
		locale: string;
		countryCode: string;
		hasShowLegislativeBodiesParam: boolean;
	},
): Promise<PetitionDetailsPageData> {
	const data = await fetchPetitionDetails(utilityContext, {
		slugOrId,
		locale,
		countryCode,
		commentsSortBy: 'SCORE',
	});

	if (!data.petition) throw new RedirectError({ url: '/?petition_not_found=true' });

	const supporterVoicesVideos = await getSupporterVoicesVideos(
		utilityContext,
		slugOrId,
		!!data.petition.videoSettingsMetadata.disableDisplay,
	);

	const petition = normalizePetition(data.petition);

	let petitionAdvocates: AdvocatesForPetitionQuery['advocatesForPetition'] = [];

	if (petition.isCommunityPetition) {
		const { advocatesForPetition } = await getPetitionAdvocates(utilityContext, {
			slugOrId: petition.id,
		});

		petitionAdvocates = advocatesForPetition;
	}

	const starterUserId = data.petition.user.id;

	return {
		petition,
		recommendedDecisionMakers: data.recommendedDecisionMakers?.items || [],
		createdLessThanOneDayAgo: differenceInDays(new Date(), new Date(data.petition.createdAt)) < 1,
		score: getPetitionStrengthScore(data.petition),
		supporterVoicesVideos,
		mediaHitsPage: {
			isLastPage: !petition.mediaHitsConnection.pageInfo.hasNextPage,
			numberOfStarterMedia: petition.mediaHitsConnection.totalCount,
			mediaHits: petition.mediaHitsConnection.nodes,
		},
		mediaSourcesPage: {
			numberOfMediaSources: petition.mediaSourcesConnection.totalCount,
			mediaSources: petition.mediaSourcesConnection.nodes,
		},
		featureConfigs: data.featureConfigs,
		petitionAdvocates,
		iOSBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppIosMwebDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		androidBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppAndroidMwebDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		desktopBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppCombinedDesktopDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
	};
}
